import React, { useEffect } from 'react';
import { Layout, Typography, Button, Space, Table, Input, Tag, Row, Col, Select, Tooltip, Switch, Card, Popconfirm, Divider } from 'antd';
import { PlusOutlined, SearchOutlined, ReloadOutlined, RobotOutlined, MessageOutlined, CalendarOutlined, ThunderboltOutlined, CloudOutlined, UserAddOutlined, RightOutlined, BulbOutlined, HistoryOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { Link, withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useCampaignsStore from '../../stores/useCampaignsStore';
import session from '../../utils/session';
import config from '../../config';
import UserAvatar from '../common/UserAvatar';
import { formatInTimezone, formatInUserTime, getTimezoneOffsetLabel } from '../../utils/campaign-timezone';
import PageTourButton from '../guide/PageTourButton';
import { getCampaignsSteps } from '../guide/toursteps';
import { getRuntimeFeatures } from '../../utils/features-runtime';
import { openUpgradeModal } from '../common/upgradeModalStore';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const getDeliveryStats = (campaign) => {
    const accepted = parseInt(campaign.accepted_count || 0);
    const delivered = parseInt(campaign.delivered_count || 0);
    const sentTotal = accepted + delivered;

    const expired = parseInt(campaign.expired_count || 0);
    const failed = parseInt(campaign.failed_count || 0);
    const totalFailed = expired + failed;

    const totalContacts = parseInt(campaign.noOfContacts || campaign.contact_group_cnt || 0);
    const unknown = totalContacts - (totalFailed + sentTotal);
    const undelivered = totalFailed + (unknown > 0 ? unknown : 0);

    return {
        accepted,
        delivered,
        sentTotal,
        expired,
        failed,
        totalContacts,
        unknown: unknown > 0 ? unknown : 0,
        undelivered
    };
};

const CampaignsV2 = ({ history }) => {
    const {
        loading,
        filteredCampaigns,
        pagination,
        filters,
        sort,
        setFilters,
        setPage,
        setSort,
        setCompanyFilter,
        updateCampaignStatus,
        deleteCampaign
    } = useCampaignsStore();

    // Starter presets stay available to any Broadcasts user — they're canned
    // campaign text, not saved templates — but one of them needs a feature
    // the create page would block, so cards can opt in via `template.feature`.
    const { hasFeature } = getRuntimeFeatures();

    // gh-admin top-bar company selector (Redux). Non-admins never see the selector,
    // so this stays null and the backend scopes by their own company.
    const selectedCompany = useSelector((state) => state.company?.selectedCompany);

    const [isAdmin, setIsAdmin] = React.useState(false);

    useEffect(() => {
        const userRole = session.get('userData')?.role;
        setIsAdmin(userRole === config.userRoles.ghAdmin);
    }, []);

    const [localSearch, setLocalSearch] = React.useState(filters.search);

    useEffect(() => {
        setLocalSearch(filters.search);
    }, [filters.search]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localSearch !== filters.search) {
                setFilters({ search: localSearch });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [localSearch]);

    // Initial load + refetch whenever the gh-admin company selection changes.
    // setCompanyFilter resets to page 1 and fetches, so this is the single fetch path.
    useEffect(() => {
        setCompanyFilter(selectedCompany || null);
    }, [selectedCompany]);

    const handleSearch = (e) => {
        setLocalSearch(e.target.value);
    };

    const handleTableChange = (pag, _tableFilters, sorter) => {
        const single = Array.isArray(sorter) ? sorter[0] : sorter;
        const nextField = single && single.order ? (single.field || single.columnKey) : 'created_at';
        const nextOrder = single && single.order === 'ascend' ? 'asc' : 'desc';
        if (nextField !== sort.field || nextOrder !== sort.order) {
            setSort(nextField, nextOrder);
        }
        setPage(pag.current, pag.pageSize);
    };

    const getSortOrder = (field) => {
        if (sort.field !== field) return null;
        return sort.order === 'asc' ? 'ascend' : 'descend';
    };

    const templates = [
        {
            key: 'shift-availability',
            title: 'Shift Availability',
            description: 'Instantly notify qualified staff about urgent open shifts requiring coverage.',
            icon: <ThunderboltOutlined style={{ color: '#D46B08' }} />,
            draft: {
                message: "Hi {{firstname}}, we have open shifts available this week. If you're interested and available to pick up a shift, please reply 'YES' and we will follow up with the schedule."
            }
        },
        {
            key: 'interview-invite',
            title: 'Interview Invite',
            description: 'Send a fast invite to shortlisted candidates and accelerate interview booking.',
            icon: <CalendarOutlined style={{ color: '#1D39C4' }} />,
            // This preset preselects a calendar-required agent, which the create
            // page won't let you submit without interview-scheduling — so gate
            // the card rather than routing to a dead end.
            feature: 'interview-scheduling',
            draft: {
                messageType: 'auto',
                moduleGroupId: config.defaultInterviewModules?.cronofy || '966065f2-1811-4c88-8278-05d4ca2711b4'
            }
        },
        {
            key: 'weather-alert',
            title: 'Weather Alert',
            description: 'Broadcast urgent office closure and safety notifications due to weather conditions.',
            icon: <CloudOutlined style={{ color: '#CF1322' }} />,
            draft: {
                message: 'Urgent: Due to severe weather conditions, our office operations are impacted today. Please prioritize your safety and monitor your email for further instructions and updates.'
            }
        },
        {
            key: 'onboarding-welcome',
            title: 'Onboarding Welcome',
            description: 'Welcome new hires with first-day instructions, directions, and key reminders.',
            icon: <UserAddOutlined style={{ color: '#237804' }} />,
            draft: {
                message: "Welcome to the team, {{firstname}}! We are thrilled to have you on board. Please check your email for your first-day schedule, login instructions, and other orientation details."
            }
        }
    ];

    const handleUseTemplate = (template) => {
        const currentDate = moment().format('MMM DD, YYYY');
        const draft = { ...template.draft };
        
        if (template.key === 'shift-availability') draft.campaignName = `Shift Availability - ${currentDate}`;
        if (template.key === 'interview-invite') draft.campaignName = `Interview Campaign - ${currentDate}`;
        if (template.key === 'weather-alert') draft.campaignName = `Weather Alert - ${currentDate}`;
        if (template.key === 'onboarding-welcome') draft.campaignName = `Onboarding: Welcome - ${currentDate}`;

        history.push('/campaign/create', {
            templateDraft: draft
        });
    };

    const getStatusParams = (campaign) => {
        let status = 'Sent';
        let color = 'green';
        
        if (campaign.is_draft) {
            status = 'Draft';
            color = 'default';
        } else if (campaign.status === false) {
            status = 'Scheduled';
            color = 'blue';
        } else if (campaign.status === null) {
            status = 'On Hold';
            color = 'orange';
        }
        if (campaign.is_inprogress) {
            status = 'Sending';
            color = 'processing';
        }
        if (!campaign.hasCampaignBot && !campaign.is_draft) {
            status = 'No Campaign Bot';
            color = 'default';
        }
        return { status, color };
    };

    const timezoneSubtextStyle = {
        marginTop: 2,
        fontSize: 11,
        color: 'var(--gh-text-secondary, #607D8B)',
        lineHeight: '14px'
    };

    const expandedRowRender = (record) => {
        const { status } = getStatusParams(record);
        const showDelivery = ['Sent', 'Sending'].includes(status);
        const stats = getDeliveryStats(record);

        return (
            <div
                style={{
                    marginLeft: 28,
                    paddingLeft: 16,
                    borderLeft: '2px solid #f0f0f0'
                }}
            >
                <Row gutter={[24, 8]}>
                    <Col xs={24} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Group</Text>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{record.group_name || '--'}</div>
                    </Col>
                    <Col xs={24} md={18}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Message</Text>
                        <div
                            style={{
                                marginTop: 2,
                                color: 'var(--gh-text-secondary, #607D8B)',
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}
                        >
                            {record.message || '--'}
                        </div>
                    </Col>
                    <Col xs={24}>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>Delivery Details</Text></div>
                        {!showDelivery ? (
                            <div style={{ fontSize: 13, marginTop: 2 }}>-</div>
                        ) : (
                            <Space size={8} wrap style={{ marginTop: 4, display: 'flex' }}>
                                <Tooltip title="Total messages accepted by the carrier or confirmed delivered. Sum of the Accepted + Delivered pills below.">
                                    <Tag color="success" style={{ margin: 0, borderRadius: 999, fontWeight: 600, fontSize: 13, padding: '2px 12px' }}>
                                        Delivered/Sent {stats.sentTotal}
                                    </Tag>
                                </Tooltip>
                                <Tooltip title={`Messages that failed, expired, or have no delivery record. Sum of the Failed + Expired${stats.unknown > 0 ? ' + Unknown' : ''} pills below.`}>
                                    <Tag color={stats.undelivered > 0 ? 'error' : 'default'} style={{ margin: 0, borderRadius: 999, fontWeight: 600, fontSize: 13, padding: '2px 12px' }}>
                                        Failed/Undelivered {stats.undelivered}
                                    </Tag>
                                </Tooltip>
                                <Divider type="vertical" style={{ height: 20, margin: '0 4px' }} />
                                <Tooltip title="Carrier confirmed the SMS arrived on the recipient's handset.">
                                    <Tag style={{ margin: 0, borderRadius: 999 }}>
                                        Delivered {stats.delivered}
                                    </Tag>
                                </Tooltip>
                                <Tooltip title="Carrier accepted the message for sending. Delivery receipt not yet received — still in flight.">
                                    <Tag style={{ margin: 0, borderRadius: 999 }}>
                                        Accepted {stats.accepted}
                                    </Tag>
                                </Tooltip>
                                <Tooltip title="Carrier reported the message could not be delivered (e.g. invalid number, blocked).">
                                    <Tag style={{ margin: 0, borderRadius: 999 }}>
                                        Failed {stats.failed}
                                    </Tag>
                                </Tooltip>
                                <Tooltip title="Message timed out in the carrier queue before it could be delivered.">
                                    <Tag style={{ margin: 0, borderRadius: 999 }}>
                                        Expired {stats.expired}
                                    </Tag>
                                </Tooltip>
                                {stats.unknown > 0 && (
                                    <Tooltip title="Contacts in this group with no delivery status on record.">
                                        <Tag style={{ margin: 0, borderRadius: 999 }}>
                                            Unknown {stats.unknown}
                                        </Tag>
                                    </Tooltip>
                                )}
                            </Space>
                        )}
                    </Col>
                    {record.is_draft && (
                        <Col xs={24} style={{ paddingTop: 8, borderTop: '1px solid #f0f0f0', marginTop: 8 }}>
                            <Popconfirm
                                title="Delete this draft?"
                                description="This action cannot be undone."
                                okText="Yes, delete"
                                okType="danger"
                                cancelText="Cancel"
                                onConfirm={() => deleteCampaign(record.id)}
                            >
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                >
                                    Delete Draft
                                </Button>
                            </Popconfirm>
                        </Col>
                    )}
                </Row>
            </div>
        );
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 300,
            render: (text, record) => (
                <div style={{ width: '100%', maxWidth: 300 }}>
                    <Link
                        to={record.is_draft ? {
                            pathname: '/campaign/create',
                            state: { draftCampaign: record }
                        } : `/campaign/${record.id}`}
                        className="gh-table-link"
                        style={{
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        title={text}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {text}
                    </Link>
                    {record.is_draft && (
                        <Tag style={{ marginLeft: 8 }} color="default">Draft</Tag>
                    )}
                </div>
            ),
            sorter: true,
            sortOrder: getSortOrder('name')
        },
        {
            title: 'Contacts',
            key: 'contacts',
            width: 110,
            responsive: ['sm'],
            render: (_, record) => {
                const count = parseInt(record.noOfContacts || record.contact_group_cnt || 0);
                const optedOut = parseInt(record.optedOutCount || 0);
                if (optedOut > 0) {
                    return (
                        <Tooltip title={`${optedOut} opted out`}>
                            <Text>{count} <Text type="secondary" style={{ fontSize: 12 }}>({optedOut} opt-out)</Text></Text>
                        </Tooltip>
                    );
                }
                return <Text>{count}</Text>;
            }
        },
        {
            title: 'Type',
            key: 'type',
            width: 180,
            render: (_, record) => {
                const hasFlow = !!record.modulegroup_id;
                const flowName = record.modulegroup_name || record.modulegroup?.name || 'Flow';
                return (
                    <Space size={6}>
                        {hasFlow ? <RobotOutlined /> : <MessageOutlined />}
                        <Text>{hasFlow ? flowName : 'Custom'}</Text>
                    </Space>
                );
            }
        },
        {
            title: 'Created By',
            key: 'creator_name',
            width: 190,
            responsive: ['md'],
            render: (_, record) => {
                const creatorName = record.creator_name || record.username || 'Unknown';
                return (
                    <Space size={8}>
                        <UserAvatar
                            name={creatorName}
                            userId={record.user_id}
                            size={22}
                            showTooltip
                        />
                        <Text>{creatorName}</Text>
                    </Space>
                );
            },
            sorter: true,
            sortOrder: getSortOrder('creator_name')
        },
        {
            title: 'Created Date',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 170,
            render: (text, record) => {
                if (!text) return '-';
                const formatted = formatInTimezone(text, record.creator_timezone);
                const timezoneLabel = getTimezoneOffsetLabel(text, record.creator_timezone);
                return (
                    <div>
                        <Tooltip title={formatInUserTime(text)}>
                            <span>{formatted}</span>
                        </Tooltip>
                        <div style={timezoneSubtextStyle}>{timezoneLabel}</div>
                    </div>
                );
            },
            sorter: true,
            sortOrder: getSortOrder('created_at')
        },
        {
            title: 'Schedule',
            dataIndex: 'schedule',
            key: 'schedule',
            width: 170,
            render: (text, record) => {
                if (!text) return '-';
                const formatted = formatInTimezone(text, record.timezone);
                return (
                    <div>
                        <Tooltip title={formatInUserTime(text)}>
                            <span>{formatted}</span>
                        </Tooltip>
                        <div style={timezoneSubtextStyle}>{record.timezone || '--'}</div>
                    </div>
                );
            },
            sorter: true,
            sortOrder: getSortOrder('schedule')
        },
        {
            title: 'Status',
            key: 'status',
            width: 120,
            render: (_, record) => {
                const { status, color } = getStatusParams(record);
                return <Tag color={color}>{status}</Tag>;
            }
        },
        ...(isAdmin ? [{
            title: 'Review Status',
            key: 'reviewStatus',
            width: 130,
            responsive: ['xl'],
            render: (_, record) => (
                <Switch 
                    checked={record.is_active} 
                    onChange={(checked) => updateCampaignStatus(record.id, checked)}
                    checkedChildren="Approved"
                    unCheckedChildren="Pending"
                />
            )
        }] : []),
    ];

    const hasRows = Array.isArray(filteredCampaigns) && filteredCampaigns.length > 0;

    return (
        <Layout className="gh-compact-page">
            <Content className="gh-compact-panel" style={{ display: 'flex', flexDirection: 'column', overflowX: 'hidden', overflowY: 'visible' }}>
                <div
                    className="tour-campaign-create"
                    style={{
                        marginBottom: 16,
                        background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
                        border: '1px solid #d9f7be',
                        padding: 20
                    }}
                >
                    <Row gutter={[16, 16]} align="middle" justify="space-between">
                        <Col xs={24} md={16}>
                            <Space direction="vertical" size={6}>
                                <Title level={3} style={{ margin: 0, color: '#1f2937' }}>Broadcast Messaging <PageTourButton steps={getCampaignsSteps()} /></Title>
                                <Text style={{ fontSize: 15, color: '#4b5563' }}>
                                    Launch from proven templates or review recent campaign performance to move faster with confidence.
                                </Text>
                                <Space size={16}>
                                    <Space size={6}>
                                        <BulbOutlined style={{ color: '#08979c' }} />
                                        <Text type="secondary">Template-led creation</Text>
                                    </Space>
                                    <Space size={6}>
                                        <HistoryOutlined style={{ color: '#08979c' }} />
                                        <Text type="secondary">{pagination.total} total campaigns</Text>
                                    </Space>
                                </Space>
                            </Space>
                        </Col>
                        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                            <Space wrap>
                                {/* <Button icon={<ReloadOutlined />} onClick={() => fetchCampaigns()}>
                                    Refresh
                                </Button> */}
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push('/campaign/create')}>
                                    New Custom Campaign
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <div style={{ padding:'0 20px', marginBottom: 8 }}>
                    <Text strong style={{ fontSize: '12px', textTransform: 'uppercase', color: '#888' }}>Start with a Template</Text>
                </div>

                <Row gutter={[16, 16]} style={{ padding:'0 20px' }} className="tour-campaign-templates">
                    {templates.map((template) => {
                        const locked = !!template.feature && !hasFeature(template.feature);
                        return (
                        <Col xs={24} sm={12} lg={6} key={template.key}>
                            <Card
                                hoverable
                                style={{ height: '100%', borderRadius: 8, opacity: locked ? 0.75 : 1 }}
                                bodyStyle={{ padding: 12, height: '100%', display: 'flex', flexDirection: 'column' }}
                                onClick={() => (locked
                                    ? openUpgradeModal(template.feature)
                                    : handleUseTemplate(template))}
                            >
                                <div style={{ display: 'flex', gap: 12, flex: 1 }}>
                                    <div
                                        style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 8,
                                            background: '#f0f2f5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        {locked ? <LockOutlined style={{ color: '#faad14' }} /> : template.icon}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: '#1f2937' }}>
                                            {template.title}
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 12, lineHeight: '16px', flex: 1 }}>
                                            {template.description}
                                        </Text>
                                        {locked ? (
                                            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', color: '#8c8c8c', fontSize: 13, fontWeight: 500 }}>
                                                Not included in your plan <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                                            </div>
                                        ) : (
                                            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', color: '#1890ff', fontSize: 13, fontWeight: 500 }}>
                                                Use Template <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        </Col>
                        );
                    })}
                </Row>
                <div style={{padding:20}}>
                    <div className="gh-compact-toolbar">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={10}>
                                <Input
                                    placeholder="Search by Name, Creator, Group, or Message"
                                    prefix={<SearchOutlined />}
                                    onChange={handleSearch}
                                    value={localSearch}
                                    allowClear
                                    disabled={loading}
                                />
                            </Col>
                            <Col xs={24} md={6}>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="Filter by Status"
                                    value={filters.status}
                                    onChange={(val) => setFilters({ status: val })}
                                    disabled={loading}
                                >
                                    <Option value="all">All Status</Option>
                                    <Option value="sent">Sent</Option>
                                    <Option value="scheduled">Scheduled</Option>
                                    <Option value="sending">Sending</Option>
                                    <Option value="draft">Draft</Option>
                                </Select>
                            </Col>
                        </Row>
                    </div>

                    <div className="gh-compact-table-wrap" style={{ overflowX: hasRows ? 'auto' : 'hidden' }}>
                        <Table
                            className="gh-themed-table"
                            tableLayout="fixed"
                            scroll={hasRows ? { x: 980 } : undefined}
                            columns={columns}
                            dataSource={filteredCampaigns}
                            rowKey="id"
                            loading={loading}
                            size="small"
                            pagination={{
                                current: pagination.page,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                            }}
                            expandable={{
                                expandedRowRender,
                                expandRowByClick: true
                            }}
                            sortDirections={['ascend', 'descend', 'ascend']}
                            onChange={handleTableChange}
                        />
                    </div>
                </div>
            </Content>
        </Layout>
    );
};

export default withRouter(CampaignsV2);
